/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store";
import _ from "lodash";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import {
  addNewBankAccountAndSetToContractor,
  assignBankAccountToContractor,
  getContractorBankAccount,
  setDefaultBankAccountContractor,
  unassignBankAccountFromContractor,
  unsetDefaultBankAccountContractor,
} from "@/requests/administration/contractors/contractorBankAccountAdministration";
import { DataOptions } from "vuetify";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";
import { FILTER_NAMES } from "@/static/filterNames";
import {
  ContractorBankAccount,
  ContractorBankAccountResponse,
} from "@/models/api/administration/contractors/ContractorBankAccountResponse";
import { BankAccount } from "@/models/api/administration/bankAccounts/BankAccountsList";

export interface AdministrationContractorBankAccountStateInterface {
  isAddNewBankAccountDialog: boolean;
  bankAccountFilters: null | Array<CriterionBase>;
  dataTableOptions: DataOptions;
  bankAccounts: Array<ContractorBankAccount>;
  selectedBankAccountId: null | number;
  countItems: null | number;
}

export const AdministrationContractorBankAccountState: AdministrationContractorBankAccountStateInterface = {
  isAddNewBankAccountDialog: false,
  bankAccountFilters: null,
  dataTableOptions: new DefaultDataOptions(),
  bankAccounts: [],
  selectedBankAccountId: null,
  countItems: null,
};

const AdministrationContractorBankAccountModule = defineModule({
  state: AdministrationContractorBankAccountState as AdministrationContractorBankAccountStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    setContractorsBankAccountFilters(state, param: Array<CriterionBase>) {
      state.bankAccountFilters = param;
    },
    setBankAccounts(state, param: ContractorBankAccountResponse) {
      state.bankAccounts = param.items;
      state.countItems = param.totalCount;
    },
    setDataTableOptions(state, param: DataOptions) {
      state.dataTableOptions = param;
    },
    setSelectedBankAccountId(state, param: number | null) {
      state.selectedBankAccountId = param;
    },
    setIsAddNewDialog(state, param: boolean) {
      state.isAddNewBankAccountDialog = param;
    },
  },
  actions: {
    async downloadContractorsBankAccount(context) {
      const { commit, state, rootState } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        const bankAccounts = await getContractorBankAccount(
          {
            count: state.dataTableOptions.itemsPerPage,
            page: state.dataTableOptions.page - 1,
            criterions: _.cloneDeep(state.bankAccountFilters),
            filterName: FILTER_NAMES.BankAccountContractorFilter,
          },
          rootState.administration.contractors.list.selectedContractorId
        );
        commit.setBankAccounts(bankAccounts);
        if (bankAccounts.items.length > 0) {
          commit.setSelectedBankAccountId(bankAccounts.items[0].id);
        }
      }
    },
    async assignToContractor(context, bankAccountId: number) {
      const { rootState, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await assignBankAccountToContractor(
          rootState.administration.contractors.list.selectedContractorId,
          bankAccountId
        );
        await rootDispatch.administration.contractors.bankAccount.downloadContractorsBankAccount();
      }
    },
    async unassignFromContractor(context, bankAccountId: number) {
      const { rootState, rootDispatch } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await rootDispatch.failIfRequestsInProgress();
        await unassignBankAccountFromContractor(
          rootState.administration.contractors.list.selectedContractorId,
          bankAccountId
        );
        await rootDispatch.administration.contractors.bankAccount.downloadContractorsBankAccount();
      }
    },
    async unsetDefaultFromContractor(context, bankAccountId: number) {
      const { rootState, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await unsetDefaultBankAccountContractor(
          rootState.administration.contractors.list.selectedContractorId,
          bankAccountId
        );
        await rootDispatch.administration.contractors.bankAccount.downloadContractorsBankAccount();
      }
    },
    async setDefaultForContractor(context, bankAccountId: number) {
      const { rootState, rootDispatch } = _moduleActionContext(context);
      await rootDispatch.failIfRequestsInProgress();
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await setDefaultBankAccountContractor(
          rootState.administration.contractors.list.selectedContractorId,
          bankAccountId
        );
        await rootDispatch.administration.contractors.bankAccount.downloadContractorsBankAccount();
      }
    },
    async addNewBankAccount(context, newBankAccount: BankAccount) {
      const { rootState, rootDispatch, commit } = _moduleActionContext(context);
      if (rootState.administration.contractors.list.selectedContractorId !== null) {
        await addNewBankAccountAndSetToContractor(
          rootState.administration.contractors.list.selectedContractorId,
          newBankAccount
        );
        await rootDispatch.popup.addOperationSuccessfulSnackbar();
        await rootDispatch.administration.contractors.bankAccount.downloadContractorsBankAccount();
        commit.setIsAddNewDialog(false);
      }
    },
  },
});

export default AdministrationContractorBankAccountModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, AdministrationContractorBankAccountModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, AdministrationContractorBankAccountModule);
